import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { useQuery } from "@tanstack/react-query"
import axios from "axios"
import { Palette, RotateCcw } from "lucide-react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import defaultTheme from "../../styles/theme.css?raw"

interface ThemeJSON {
  id: string // Added id to match UI usage
  name: string
  type: string
  css: {
    [key: string]: {
      [selector: string]: {
        [property: string]: string
      }
    }
  }
  cssVars: {
    theme?: { [key: string]: string }
    light: { [key: string]: string }
    dark: { [key: string]: string }
  }
}

const applyTheme = (themeJSON: ThemeJSON) => {
  const existingStyle = document.getElementById("custom-theme")
  if (existingStyle) existingStyle.remove()

  let cssContent = ""
  const { cssVars, css } = themeJSON

  // Base theme (shared)
  cssContent += ":root {\n"
  if (cssVars.theme) {
    Object.entries(cssVars.theme).forEach(([key, value]) => {
      cssContent += `  --${key}: ${value};\n`
    })
  }
  cssContent += "}\n\n"

  // Light theme
  if (cssVars.light) {
    cssContent += `:root.light {\n`
    Object.entries(cssVars.light).forEach(([key, value]) => {
      cssContent += `  --${key}: ${value};\n`
    })
    cssContent += "}\n\n"
  }

  // Dark theme
  if (cssVars.dark) {
    cssContent += `:root.dark {\n`
    Object.entries(cssVars.dark).forEach(([key, value]) => {
      cssContent += `  --${key}: ${value};\n`
    })
    cssContent += "}\n\n"
  }

  // Extra custom CSS layers
  Object.entries(css).forEach(([layer, selectors]) => {
    cssContent += `@layer ${layer} {\n`
    Object.entries(selectors).forEach(([selector, properties]) => {
      cssContent += `  ${selector} {\n`
      Object.entries(properties).forEach(([property, value]) => {
        const cssProperty = property.replace(/([A-Z])/g, "-$1").toLowerCase()
        cssContent += `    ${cssProperty}: ${value};\n`
      })
      cssContent += "  }\n"
    })
    cssContent += "}\n\n"
  })

  if (cssContent.trim()) {
    const style = document.createElement("style")
    style.id = "custom-theme"
    style.textContent = cssContent
    document.head.appendChild(style)
    localStorage.setItem("custom-theme-json", JSON.stringify(themeJSON))
  }
}

const applyDefaultTheme = () => {
  const existingStyle = document.getElementById("custom-theme")
  if (existingStyle) {
    existingStyle.remove()
  }
  const style = document.createElement("style")
  style.id = "custom-theme"
  style.textContent = defaultTheme
  document.head.appendChild(style)
}

function kebabToTitleCase(kebab: string) {
  return kebab
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ")
}

const resetTheme = () => {
  const existingStyle = document.getElementById("custom-theme")
  if (existingStyle) {
    existingStyle.remove()
  }

  const style = document.createElement("style")
  style.id = "custom-theme"
  style.textContent = defaultTheme // Use imported default theme
  document.head.appendChild(style)

  localStorage.removeItem("custom-theme-json")
}

export default function ThemePopover({
  popoverClassName,
}: {
  popoverClassName?: string
}) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedThemeId, setSelectedThemeId] = useState<string | null>(null)

  useEffect(() => {
    const savedJson = localStorage.getItem("custom-theme-json")
    if (savedJson) {
      try {
        const themeJSON = JSON.parse(savedJson) as ThemeJSON
        applyTheme(themeJSON)
      } catch (e) {
        console.error("Failed to load saved theme", e)
        applyDefaultTheme()
      }
    } else {
      const defaultThemeEnv = import.meta.env.VITE_DEFAULT_THEME
      if (defaultThemeEnv) {
        axios
          .get("/tweakcn-themes.json")
          .then((resp) => {
            const themes = resp.data as ThemeJSON[]
            const theme = themes.find((t) => t.name === defaultThemeEnv)
            if (theme) {
              applyTheme(theme)
            } else {
              console.warn(
                `Default theme "${defaultThemeEnv}" not found in tweakcn-themes.json`,
              )
              applyDefaultTheme()
            }
          })
          .catch(() => {
            applyDefaultTheme()
          })
      } else {
        applyDefaultTheme()
      }
    }
  }, [])

  const loadThemes = async () => {
    const resp = await axios.get("/tweakcn-themes.json")
    return resp.data
  }

  const {
    data: themes,
    isLoading,
    isError,
    error,
  } = useQuery<ThemeJSON[]>({
    queryKey: ["themes"],
    queryFn: loadThemes,
  })

  useEffect(() => {
    if (themes && selectedThemeId === null) {
      const savedJson = localStorage.getItem("custom-theme-json")
      if (savedJson) {
        try {
          const savedTheme = JSON.parse(savedJson) as ThemeJSON
          const matchingTheme = themes.find(
            (theme) => theme.name === savedTheme.name,
          )
          if (matchingTheme) {
            setSelectedThemeId(
              matchingTheme.id || themes.indexOf(matchingTheme).toString(),
            )
          }
        } catch (e) {
          console.error("Failed to match saved theme", e)
        }
      }
    }
  }, [themes, selectedThemeId])

  const filteredThemes =
    themes?.filter((theme) =>
      theme.name.toLowerCase().includes(searchQuery.toLowerCase()),
    ) || []

  if (isLoading || !filteredThemes) {
    return <div>Loading...</div>
  }

  if (isError) {
    return <div>Error: {error?.message}</div>
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button className="cursor-pointer bg-transparent" variant="outline">
          <Palette className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          "bg-background border-border m-5 w-72 p-0",
          popoverClassName,
        )}
        align="end"
      >
        {/* Search Input */}
        <div className="p-4 pb-3">
          <div className="relative">
            <Input
              placeholder={t("Search themes...")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-input border-border text-foreground placeholder:text-muted-foreground pl-8"
            />
            <svg
              className="text-muted-foreground absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
          </div>
        </div>
        {/* Theme Count and Actions */}
        <div className="flex items-center justify-between px-4 pb-3">
          <span className="text-muted-foreground text-sm">
            {filteredThemes.length} {t("themes")}
          </span>
          <Button
            onClick={resetTheme}
            variant="outline"
            size="sm"
            className="cursor-pointer"
          >
            <RotateCcw className="mr-1 h-4 w-4" />
            {t("reset")}
          </Button>
        </div>
        {/* Built-in Themes Section */}
        <div className="px-4 pb-2">
          <h3 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
            {t("Built-in Themes")}
          </h3>
        </div>
        {/* Theme List */}
        <div className="max-h-[300px] overflow-y-auto">
          {filteredThemes.map((theme, index) => (
            <button
              key={theme.id || index}
              onClick={() => {
                setSelectedThemeId(theme.id || index.toString())
                applyTheme(theme)
              }}
              className={`hover:bg-secondary flex w-full items-center gap-3 px-4 py-3 transition-colors ${
                selectedThemeId === (theme.id || index.toString())
                  ? "bg-ring"
                  : ""
              }`}
            >
              {/* Color Swatches */}
              <div className="flex gap-1">
                {[
                  theme.cssVars.dark?.accent,
                  theme.cssVars.dark?.ring,
                  theme.cssVars.light?.card,
                  theme.cssVars.light?.foreground,
                ].map((color, colorIndex) => (
                  <div
                    key={colorIndex}
                    className="h-4 w-4 rounded-sm"
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>

              {/* Theme Name */}
              <span
                className={`text-sm font-medium ${
                  selectedThemeId === (theme.id || index.toString())
                    ? "text-primary-foreground"
                    : "text-foreground"
                }`}
              >
                {kebabToTitleCase(theme.name)}
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
